import React from "react";

/*
 * Titan Infra — official logo, vector, reconstructed from the video artefact.
 *
 * Usage:
 *   <Logo />                              // full: icon + wordmark, auto theme
 *   <Logo variant="icon" size={32} />     // icon only (favicon-scale)
 *   <Logo theme="light" />                // force light-on-dark rendering
 *   <Logo theme="dark"  />                // force dark-on-light rendering
 *
 * The icon is a fully vector isometric hex-cube in SVG (top face silver,
 * left face deep blue, right face brand blue), matching the video reveal.
 * The wordmark uses the app's display font ("General Sans" fallback) so it
 * inherits your typography stack — no font baked into the SVG.
 */

const GRADIENTS = () => (
  <defs>
    <linearGradient id="titan-top" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%"   stopColor="#F1F5FA" />
      <stop offset="100%" stopColor="#B4C2D3" />
    </linearGradient>
    <linearGradient id="titan-left" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%"   stopColor="#1E4A7A" />
      <stop offset="100%" stopColor="#0B2648" />
    </linearGradient>
    <linearGradient id="titan-right" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%"   stopColor="#5FB2EE" />
      <stop offset="100%" stopColor="#1E70C5" />
    </linearGradient>
    <linearGradient id="titan-text-blue" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%"   stopColor="#5FB2EE" />
      <stop offset="100%" stopColor="#1E70C5" />
    </linearGradient>
  </defs>
);

/*
 * Hex-cube geometry — hexagon inscribed in a 100×100 box (viewBox 0 0 100 100).
 * Six outer vertices + centre, split into three rhombic faces.
 */
const HEX = {
  top:      "50,6  90,29 50,52 10,29",   // top face
  left:     "10,29 50,52 50,94 10,71",   // left face
  right:    "90,29 50,52 50,94 90,71",   // right face
  // Highlight edges to reproduce the metallic reveal from the video
  edgeTop:  "M50,6 L90,29",
  edgeCore: "M10,29 L50,52 L90,29",
  edgeMid:  "M50,52 L50,94",
};

export const LogoIcon = ({ size = 32, className = "", strokeAccent = true, ...rest }) => (
  <svg
    width={size} height={size} viewBox="0 0 100 100" fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-label="Titan Infra"
    {...rest}
  >
    <GRADIENTS />
    <polygon points={HEX.left}  fill="url(#titan-left)" />
    <polygon points={HEX.right} fill="url(#titan-right)" />
    <polygon points={HEX.top}   fill="url(#titan-top)" />
    {strokeAccent && (
      <g stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" strokeLinecap="round" fill="none">
        <path d={HEX.edgeTop} />
        <path d={HEX.edgeCore} />
        <path d={HEX.edgeMid} />
      </g>
    )}
  </svg>
);

/*
 * Full lockup: icon + wordmark side-by-side.
 * theme:
 *   "auto"  → wordmark uses currentColor for "Titan" and muted for "Infra"
 *   "light" → optimised for dark backgrounds (Titan in blue gradient, Infra light gray)
 *   "dark"  → optimised for light backgrounds (Titan in blue gradient, Infra dark gray)
 */
export const Logo = ({
  variant = "full",         // "full" | "icon" | "stacked"
  theme = "light",          // "light" (default for dark bg) | "dark"
  size = 36,                // icon size in px
  className = "",
  showTagline = false,
  tagline = "The infrastructure behind AI",
}) => {
  if (variant === "icon") return <LogoIcon size={size} className={className} />;

  const infraColour = theme === "dark" ? "text-zinc-600" : "text-zinc-400";
  const stack = variant === "stacked";

  return (
    <span
      className={`inline-flex ${stack ? "flex-col items-start gap-2" : "items-center gap-3"} ${className}`}
      aria-label="Titan Infra"
    >
      <LogoIcon size={size} />
      <span className="inline-flex flex-col leading-none">
        <span
          className="font-display font-medium tracking-tight"
          style={{
            fontSize: `${Math.round(size * 0.72)}px`,
            lineHeight: 1,
            letterSpacing: "-0.01em",
          }}
        >
          <span style={{ background: "linear-gradient(90deg,#5FB2EE 0%,#1E70C5 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
            Titan
          </span>
          <span className={`ml-1.5 font-light ${infraColour}`}>Infra</span>
        </span>
        {showTagline && (
          <span
            className={`mt-2 uppercase tracking-[0.22em] ${theme === "dark" ? "text-zinc-500" : "text-zinc-400"}`}
            style={{ fontSize: `${Math.max(9, Math.round(size * 0.22))}px` }}
          >
            {tagline}
          </span>
        )}
      </span>
    </span>
  );
};

export default Logo;
